import PageTemplateData from '@v4/utils/data/PageTemplateData'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import withLazy from '@v4/components/common/WithLazy'
import { lazy } from 'react'
import { SECTION_VARIANTS } from '@v4/components/sections/Sections.const'
import { MODULES } from './Default.modules.client'

export const SectionManager = (() => {
	const getVariantKey = (section, name) => [section, name].join('/')
	const sections = new Map()
	const variants = new Map()
	const sectionNames = {
		[PageTemplateData.SECTION_TYPES.HEADER]: 'Header',
		[PageTemplateData.SECTION_TYPES.FOOTER]: 'Footer',
		[PageTemplateData.SECTION_TYPES.BANNER]: 'Banner',
		[PageTemplateData.SECTION_TYPES.CONTENT]: 'Content',
		[PageTemplateData.SECTION_TYPES.TESTIMONIAL]: 'Testimonials',
		[PageTemplateData.SECTION_TYPES.CALL_TO_ACTION]: 'CallToAction',
		[PageTemplateData.SECTION_TYPES.CATEGORY]: 'Category',
		[PageTemplateData.SECTION_TYPES.HTML_BLOCK]: 'HtmlBlock',
		[PageTemplateData.SECTION_TYPES.VIDEO]: 'Video',
		[PageTemplateData.SECTION_TYPES.FORM]: 'Form',
		[PageTemplateData.SECTION_TYPES.POPUP]: 'Popup',
		[PageTemplateData.SECTION_TYPES.FAQ]: 'FAQ',
		[PageTemplateData.SECTION_TYPES.QUESTION]: 'Question',
		[PageTemplateData.SECTION_TYPES.QUESTION_PROGRESS]: 'QuestionProgress',
		[PageTemplateData.SECTION_TYPES.CATEGORIES_SCORES]: 'CategoryScores',
		[PageTemplateData.SECTION_TYPES.OVER_ALL_SCORE]: 'OverAllScore',
		[PageTemplateData.SECTION_TYPES.INDIVIDUAL_CATEGORY]: 'IndividualCategory',
		[PageTemplateData.SECTION_TYPES.SMART_SECTIONS]: 'SmartSections',
	}

	const sectionModules = MODULES.sections
	const variantModules = MODULES.variants

	const load = async (section, variant) => {
		const sectionName = sectionNames[section]
		if (!sectionName) return

		if (!sections.has(section)) {
			const sectionState = {
				loading: true,
				component: null,
				error: false,
			}

			sections.set(section, sectionState)
			try {
				const component = await resolvePageComponent(
					`/resources/react/v4/components/sections/${sectionName}/${sectionName}.jsx`,
					sectionModules,
				)

				sectionState.loading = false
				sectionState.component = component.default || component
				// eslint-disable-next-line no-empty
			} catch (e) {
				sectionState.loading = false
				sectionState.error = true
			}
		}

		if (!variant) {
			return
		}

		const variantKey = getVariantKey(section, variant)

		const variantName = _.get(SECTION_VARIANTS, [section, variant])

		if (!variantName || variants.has(variantKey)) {
			return
		}

		const variantState = {
			loading: true,
			component: null,
			error: false,
		}

		variants.set(variantKey, variantState)

		try {
			const component = await resolvePageComponent(
				`/resources/react/v4/components/sections/${sectionName}/Variants/${variantName}/${variantName}.jsx`,
				variantModules,
			)

			variantState.loading = false
			variantState.component = component.default || component
			// eslint-disable-next-line no-empty
		} catch (e) {
			variantState.loading = false
			variantState.error = true
		}
	}

	const getVariant = (section, variant) => {
		const variantKey = getVariantKey(section, variant)

		let component = variants.get(variantKey)?.component

		if (component) {
			return component
		}

		const sectionName = sectionNames[section]
		const variantName = _.get(SECTION_VARIANTS, [section, variant])

		if (!sectionName || !variantName) {
			return undefined
		}

		const module =
			variantModules[
				`/resources/react/v4/components/sections/${sectionName}/Variants/${variantName}/${variantName}.jsx`
			]
		if (module && window) {
			component = withLazy(lazy(module))

			variants.set(variantKey, {
				loading: false,
				component,
				error: false,
			})
		}

		return component
	}

	const getSection = section => {
		let component = sections.get(section)?.component
		if (!component) {
			const componentName = sectionNames[section]
			const module =
				componentName &&
				sectionModules[`/resources/react/v4/components/sections/${componentName}/${componentName}.jsx`]
			if (module && window) {
				component = withLazy(lazy(module))

				sections.set(section, {
					loading: false,
					component,
					error: false,
				})
			}
		}

		return component
	}

	const getAllSections = () => {
		return _.reduce(
			sectionNames,
			(acc, name, key) => {
				const component = getSection(key)

				if (component) {
					acc[key] = {
						component,
						getVariantComponent: variant => getVariant(key, variant),
					}
				}

				return acc
			},
			{},
		)
	}

	return {
		load,
		getSection,
		getAllSections,
		getVariant,
	}
})()
